import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  api,
  type CatalogoEtiquetas,
  type CatalogoManual,
  type EtiquetaPlantilla,
  type MaterialManual,
} from '../api';
import { mensajeDeError, useApi } from '../lib/useApi';
import { Cargando, Encabezado, Icono, Numero, PanelError, Vacio } from '../components/ui';
import Etiqueta, { variablesDe } from '../components/Etiqueta';
import { imprimirEtiquetas } from '../lib/imprimir';
import { abrir, desplegar } from '../components/movimiento';
import { altoMinimo, aviso, botonPrimario, botonSecundario, c, campo, etiquetaCampo, tarjeta } from '../theme';

/**
 * Etiqueta cargada a mano.
 *
 * El camino normal es Recepción: el sistema ya sabe el pallet, el artículo y el
 * proveedor, y la etiqueta se llena sola. Mientras ZF no mande el maestro de
 * artículos eso no se puede hacer, así que hace falta una pantalla donde se
 * tipee todo y se imprima igual.
 *
 * No es un atajo ni un modo de prueba: es la puerta de emergencia para poder
 * seguir etiquetando mercadería sin datos del otro lado. Por eso **no toca el
 * stock ni deja rastro**: lo único que hace es dibujar e imprimir. El pallet se
 * carga al sistema por su camino, cuando haya con qué.
 *
 * Lo que sí guarda es el catálogo: el proveedor y el material que se cargan acá
 * quedan para la próxima, y lo que se tipeó en los demás campos se ofrece de
 * vuelta. La factura de un contenedor se repite en todos sus pallets, así que
 * eso solo ahorra la mayor parte de la carga.
 */

/**
 * Las variables que el sistema completa solo, con la fecha y la hora del
 * depósito. No se preguntan: escribirlas a mano sería poder equivocarlas.
 */
const DEL_SISTEMA = ['fecha', 'hora', 'mes', 'usuario', 'deposito'];

/**
 * Los campos que el catálogo resuelve con un combo en vez de un renglón libre,
 * y de dónde sale cada uno. Elegir el proveedor completa su número y filtra los
 * materiales; elegir el material completa su nombre.
 */
const DEL_CATALOGO = {
  proveedor: 'manual.proveedor',
  nroProveedor: 'manual.nroproveedor',
  codigo: 'manual.codigo',
  material: 'manual.material',
} as const;

/** Una plantilla se puede usar si está activa y tiene algo dibujado. */
const usable = (e: EtiquetaPlantilla) => e.activa && e.plantilla.elementos.length > 0;

/**
 * Lo que hay que preguntar además de los combos del catálogo: el resto de la
 * carga manual —siempre, aunque la plantilla no lo use— y cualquier otra
 * variable que la plantilla necesite y el sistema no sepa.
 *
 * Los de carga manual van siempre porque son el formulario de la mercadería y
 * se cargan de corrido; los demás salen de la plantilla, así una etiqueta que
 * además lleva el remito no se imprime con el renglón en blanco.
 */
function campos(catalogo: CatalogoEtiquetas, plantilla: EtiquetaPlantilla | null) {
  const usadas = plantilla ? variablesDe(plantilla.plantilla) : [];
  const delCatalogo = new Set<string>(Object.values(DEL_CATALOGO));

  const sueltos = catalogo.manuales
    .filter((m) => !delCatalogo.has(m.clave))
    .map((m) => ({ ...m, enLaEtiqueta: usadas.includes(m.clave), opcional: false }));

  const delCatalogoDeVariables = Object.values(catalogo.variables)
    .flat()
    .flatMap((g) => g.variables);
  const nombres = new Map(delCatalogoDeVariables.map((v) => [v.clave, v.nombre] as const));
  const opcionales = new Set(delCatalogoDeVariables.filter((v) => v.opcional).map((v) => v.clave));
  const yaEstan = new Set(catalogo.manuales.map((m) => m.clave));

  const otras = usadas
    .filter((clave) => !yaEstan.has(clave) && !DEL_SISTEMA.includes(clave))
    .map((clave) => ({
      clave,
      nombre: nombres.get(clave) ?? clave,
      enLaEtiqueta: true,
      opcional: opcionales.has(clave),
    }));

  return { sueltos, otras, usadas };
}

// ------------------------------------------------------------------ formulario

function Renglon({
  nombre,
  enLaEtiqueta,
  children,
}: {
  nombre: string;
  enLaEtiqueta: boolean;
  children: React.ReactNode;
}) {
  return (
    <motion.label initial={false} animate={{ opacity: enLaEtiqueta ? 1 : 0.55 }} style={etiquetaCampo}>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        {nombre}
        {/* Un campo que la plantilla no dibuja se puede llenar igual, pero más
            vale decirlo que dejar creer que va a salir impreso. */}
        <AnimatePresence initial={false}>
          {!enLaEtiqueta && (
            <motion.span
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -6 }}
              transition={{ duration: 0.22 }}
              style={{ fontSize: 12, fontWeight: 400, color: c.texto4, textTransform: 'none' }}
            >
              no está en esta etiqueta
            </motion.span>
          )}
        </AnimatePresence>
      </span>
      {children}
    </motion.label>
  );
}

/**
 * Un combo que además deja cargar lo que no está.
 *
 * Es la pieza que hace que el catálogo no estorbe: si el material no figura, se
 * carga desde acá y se sigue imprimiendo. Un depósito no puede quedarse
 * esperando un alta con el pallet en el muelle.
 */
function ComboConAlta({
  valor,
  opciones,
  vacio,
  onElegir,
  onCargar,
  cargando,
}: {
  valor: string;
  opciones: { valor: string; texto: string }[];
  vacio: string;
  onElegir: (v: string) => void;
  onCargar: () => void;
  cargando: boolean;
}) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <select value={valor} onChange={(e) => onElegir(e.target.value)} style={{ ...campo, flex: 1, minWidth: 0 }}>
        <option value="">{opciones.length === 0 ? vacio : '— elegir —'}</option>
        {opciones.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.texto}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={onCargar}
        disabled={cargando}
        title="Cargar uno nuevo"
        style={{ ...botonSecundario, width: 40, minHeight: altoMinimo(42), padding: 0, display: 'grid', placeItems: 'center' }}
      >
        <Icono nombre={cargando ? 'hourglass_empty' : 'add'} size={18} color={c.texto3} />
      </button>
    </div>
  );
}

/** Un renglón libre, con lo último que se tipeó ahí ofrecido en la lista. */
function CampoConRecientes({
  clave,
  valor,
  recientes,
  onCambiar,
}: {
  clave: string;
  valor: string;
  recientes: string[];
  onCambiar: (v: string) => void;
}) {
  const listaId = `wms-rec-${clave.replace(/\W/g, '-')}`;
  return (
    <>
      <input value={valor} onChange={(e) => onCambiar(e.target.value)} list={listaId} style={campo} />
      {recientes.length > 0 && (
        <datalist id={listaId}>
          {recientes.map((r) => (
            <option key={r} value={r} />
          ))}
        </datalist>
      )}
    </>
  );
}

// ----------------------------------------------------------------- la pantalla

export default function EtiquetaManual() {
  const { datos, cargando, error, recargar } = useApi(async () => {
    const [catalogo, lista, manual] = await Promise.all([
      api.catalogoEtiquetas(),
      api.etiquetas(),
      api.catalogoManual(),
    ]);
    return { catalogo, etiquetas: lista.etiquetas.filter(usable), manual };
  }, []);

  const [elegida, setElegida] = useState<number | null>(null);
  const [valores, setValores] = useState<Record<string, string>>({});
  const [proveedorId, setProveedorId] = useState<number | null>(null);
  const [copias, setCopias] = useState('1');
  const [emitida, setEmitida] = useState<{ plantilla: EtiquetaPlantilla; datos: Record<string, string> } | null>(null);
  const [problema, setProblema] = useState('');
  const [guardando, setGuardando] = useState('');
  // El catálogo se recarga solo cuando se le agrega algo, sin volver a pedir
  // las plantillas ni el catálogo de etiquetas.
  const [manual, setManual] = useState<CatalogoManual | null>(null);

  if (error) return <PanelError mensaje={error} onReintentar={recargar} />;
  if (cargando && !datos) return <Cargando texto="Buscando las etiquetas…" />;
  if (!datos) return null;

  const { catalogo, etiquetas } = datos;
  const cat = manual ?? datos.manual;

  // Si no eligió nada, la que está en uso para pallets: es la que se pega a la
  // mercadería al recibirla, que es de lo que se trata esta pantalla.
  const plantilla =
    etiquetas.find((e) => e.id === elegida) ??
    etiquetas.find((e) => e.predeterminada && e.uso === 'pallet') ??
    etiquetas.find((e) => e.predeterminada) ??
    etiquetas[0] ??
    null;

  const lista = campos(catalogo, plantilla);
  const proveedor = cat.proveedores.find((p) => p.id === proveedorId) ?? null;
  const materiales = cat.materiales.filter((m) => m.proveedorId === proveedorId);

  const puesto = (clave: string) => (valores[clave] ?? '').trim();
  const usa = (clave: string) => lista.usadas.includes(clave);

  // Falta lo que la etiqueta dibuja y está vacío. Los derivados —el número del
  // proveedor, el nombre del material— no se reclaman: los completa el
  // catálogo, y si el catálogo no los tiene se pueden dejar en blanco.
  const faltan = [
    ...(usa(DEL_CATALOGO.proveedor) && !puesto(DEL_CATALOGO.proveedor) ? [{ nombre: 'el proveedor' }] : []),
    ...(usa(DEL_CATALOGO.codigo) && !puesto(DEL_CATALOGO.codigo) ? [{ nombre: 'el material' }] : []),
    ...[...lista.sueltos, ...lista.otras]
      .filter((f) => f.enLaEtiqueta && !f.opcional && !puesto(f.clave))
      .map((f) => ({ nombre: f.nombre.toLowerCase() })),
  ];

  const cantidad = Math.max(1, Math.min(100, Math.trunc(Number(copias)) || 1));

  function poner(campos: Record<string, string>) {
    setValores((antes) => ({ ...antes, ...campos }));
    setEmitida(null);
  }

  function elegirProveedor(id: number | null) {
    const p = cat.proveedores.find((x) => x.id === id) ?? null;
    setProveedorId(id);
    // Cambiar de proveedor limpia el material: el de antes es de otro.
    poner({
      [DEL_CATALOGO.proveedor]: p?.nombre ?? '',
      [DEL_CATALOGO.nroProveedor]: p?.numero ?? '',
      [DEL_CATALOGO.codigo]: '',
      [DEL_CATALOGO.material]: '',
    });
  }

  function elegirMaterial(codigo: string) {
    const m = materiales.find((x) => x.codigo === codigo) ?? null;
    poner({ [DEL_CATALOGO.codigo]: m?.codigo ?? '', [DEL_CATALOGO.material]: m?.nombre ?? '' });
  }

  /** Alta de proveedor desde la misma pantalla, sin salir de lo que se estaba haciendo. */
  async function cargarProveedor() {
    const nombre = window.prompt('Nombre del proveedor')?.trim();
    if (!nombre) return;
    const numero = window.prompt(`Nº de proveedor de «${nombre}» (se puede dejar vacío)`)?.trim() ?? '';

    setGuardando('proveedor');
    setProblema('');
    try {
      const creado = await api.crearProveedorManual(nombre, numero);
      const fresco = await api.catalogoManual();
      setManual(fresco);
      setProveedorId(creado.id);
      poner({
        [DEL_CATALOGO.proveedor]: creado.nombre,
        [DEL_CATALOGO.nroProveedor]: creado.numero,
        [DEL_CATALOGO.codigo]: '',
        [DEL_CATALOGO.material]: '',
      });
    } catch (err) {
      setProblema(mensajeDeError(err));
    } finally {
      setGuardando('');
    }
  }

  async function cargarMaterial() {
    if (!proveedor) return setProblema('Elegí primero el proveedor: el material se guarda con el suyo.');

    // Los dos campos son distintos y se confunden todo el tiempo: el código es
    // el del producto (1721423040) y el nombre es el material (TUBE). Cargarlo
    // al revés deja el catálogo identificado por el nombre, que es de donde
    // vienen los materiales que hoy hay que renombrar desde Artículos.
    const codigo = window.prompt('Código del material — el código del producto, ej. 1721423040')?.trim();
    if (!codigo) return;
    const nombre = window.prompt(`Nombre del material «${codigo}» — ej. TUBE`)?.trim() ?? '';

    setGuardando('material');
    setProblema('');
    try {
      const creado: MaterialManual = await api.crearMaterialManual(codigo, nombre, proveedor.id);
      const fresco = await api.catalogoManual();
      setManual(fresco);
      poner({ [DEL_CATALOGO.codigo]: creado.codigo, [DEL_CATALOGO.material]: creado.nombre });
    } catch (err) {
      setProblema(mensajeDeError(err));
    } finally {
      setGuardando('');
    }
  }

  async function generar() {
    if (!plantilla) return;
    setProblema('');
    try {
      // Se vuelve a pedir el catálogo de etiquetas para que la fecha y la hora
      // sean las de ahora y no las de cuando se abrió la pantalla: si alguien
      // deja la pestaña abierta toda la mañana, la etiqueta saldría con la hora
      // vieja.
      const fresco = await api.catalogoEtiquetas();
      const sistema = Object.fromEntries(DEL_SISTEMA.map((k) => [k, fresco.muestra[k] ?? '']));
      const cargados = Object.fromEntries(Object.entries(valores).map(([k, v]) => [k, v.trim()]));
      setEmitida({ plantilla, datos: { ...sistema, ...cargados } });
    } catch (err) {
      setEmitida(null);
      setProblema(mensajeDeError(err));
    }
  }

  function imprimir() {
    const svg = document.getElementById('wms-etq-manual')?.innerHTML ?? '';
    if (!svg) return setProblema('No se pudo tomar el dibujo de la etiqueta. Generala de nuevo.');
    setProblema('');

    const fallo = imprimirEtiquetas(emitida!.plantilla, Array.from({ length: cantidad }, () => svg));
    if (fallo) return setProblema(fallo);

    // Se anota al imprimir y no al escribir: lo que quedó a medio tipear y se
    // borró no es un valor que alguien vaya a querer de vuelta. Y si falla, no
    // se le dice nada a nadie: la etiqueta ya salió, y perder la sugerencia de
    // la próxima no es un problema del que está imprimiendo.
    void api
      .anotarUsadosManual(emitida!.datos)
      .then(() => api.catalogoManual())
      .then(setManual)
      .catch(() => {});
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Encabezado
        bajada="Para etiquetar mercadería de la que el sistema todavía no tiene los datos: se cargan los campos a mano y se imprime. El proveedor y el material quedan guardados para la próxima. No modifica el stock."
      />

      {etiquetas.length === 0 ? (
        <Vacio
          icono="label_off"
          titulo="No hay ninguna etiqueta para usar"
          detalle="Hace falta una plantilla activa y dibujada. Un administrador la crea en Etiquetas."
        />
      ) : (
        <>
          <AnimatePresence>
            {problema && (
              <motion.div key={problema} role="alert" {...abrir(8)} style={aviso('err')}>
                {problema}
              </motion.div>
            )}
          </AnimatePresence>

          <div style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label style={{ ...etiquetaCampo, flex: 1, minWidth: 220 }}>
                Etiqueta
                <select
                  value={plantilla?.id ?? ''}
                  onChange={(e) => {
                    setElegida(Number(e.target.value));
                    setEmitida(null);
                  }}
                  style={campo}
                >
                  {etiquetas.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.nombre} · {e.uso === 'pallet' ? 'pallet' : 'capacho'} · {e.ancho} × {e.alto} mm
                      {e.predeterminada ? ' · en uso' : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ ...etiquetaCampo, width: 120 }}>
                Cantidad
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={100}
                  value={copias}
                  onChange={(e) => setCopias(e.target.value)}
                  style={campo}
                />
              </label>
            </div>

            {/* ------------------------------------------------ la mercadería */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <span style={{ fontSize: 14, fontWeight: 600, color: c.texto }}>Mercadería</span>
                <div style={{ marginTop: 2, fontSize: 12.5, color: c.texto3, textWrap: 'pretty' }}>
                  Del catálogo del depósito. Si algo no está en la lista, cargalo con el «+» y queda guardado.
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 10 }}>
                <Renglon nombre="Proveedor" enLaEtiqueta={usa(DEL_CATALOGO.proveedor)}>
                  <ComboConAlta
                    valor={proveedorId === null ? '' : String(proveedorId)}
                    opciones={cat.proveedores.map((p) => ({
                      valor: String(p.id),
                      texto: p.numero ? `${p.nombre} · ${p.numero}` : p.nombre,
                    }))}
                    vacio="— todavía no hay proveedores —"
                    onElegir={(v) => elegirProveedor(v ? Number(v) : null)}
                    onCargar={cargarProveedor}
                    cargando={guardando === 'proveedor'}
                  />
                </Renglon>

                <Renglon nombre="Nº de proveedor" enLaEtiqueta={usa(DEL_CATALOGO.nroProveedor)}>
                  {/* Sale del proveedor elegido, pero se puede corregir: es más
                      barato pisarlo acá que dejar de imprimir por un dato
                      cargado mal. */}
                  <input
                    value={valores[DEL_CATALOGO.nroProveedor] ?? ''}
                    onChange={(e) => poner({ [DEL_CATALOGO.nroProveedor]: e.target.value })}
                    placeholder={proveedor ? '' : 'elegí el proveedor'}
                    style={campo}
                  />
                </Renglon>

                <Renglon nombre="Código de material" enLaEtiqueta={usa(DEL_CATALOGO.codigo)}>
                  <ComboConAlta
                    valor={puesto(DEL_CATALOGO.codigo)}
                    // Sólo el código: el nombre ya tiene su campo al lado, y
                    // «1721422002 · TUBE» en éste confundía cuál era cuál.
                    opciones={materiales.map((m) => ({ valor: m.codigo, texto: m.codigo }))}
                    vacio={proveedor ? '— este proveedor no tiene materiales —' : '— elegí el proveedor —'}
                    onElegir={elegirMaterial}
                    onCargar={cargarMaterial}
                    cargando={guardando === 'material'}
                  />
                </Renglon>

                <Renglon nombre="Nombre del material" enLaEtiqueta={usa(DEL_CATALOGO.material)}>
                  <input
                    value={valores[DEL_CATALOGO.material] ?? ''}
                    onChange={(e) => poner({ [DEL_CATALOGO.material]: e.target.value })}
                    placeholder={puesto(DEL_CATALOGO.codigo) ? '' : 'elegí el material'}
                    style={campo}
                  />
                </Renglon>
              </div>
            </div>

            {/* ------------------------------------------------- lo del ingreso */}
            <AnimatePresence initial={false}>
            {lista.sueltos.length > 0 && (
              <motion.div {...desplegar} style={{ ...desplegar.style, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <span style={{ fontSize: 14, fontWeight: 600, color: c.texto }}>Datos del ingreso</span>
                  <div style={{ marginTop: 2, fontSize: 12.5, color: c.texto3, textWrap: 'pretty' }}>
                    De este ingreso, no del catálogo. La lista ofrece lo último que se usó.
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 10 }}>
                  {lista.sueltos.map((f) => (
                    <Renglon key={f.clave} nombre={f.nombre} enLaEtiqueta={f.enLaEtiqueta}>
                      <CampoConRecientes
                        clave={f.clave}
                        valor={valores[f.clave] ?? ''}
                        recientes={cat.recientes[f.clave] ?? []}
                        onCambiar={(v) => poner({ [f.clave]: v })}
                      />
                    </Renglon>
                  ))}
                </div>
              </motion.div>
            )}
            </AnimatePresence>

            <AnimatePresence initial={false}>
            {lista.otras.length > 0 && (
              <motion.div {...desplegar} style={{ ...desplegar.style, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <span style={{ fontSize: 14, fontWeight: 600, color: c.texto }}>
                    Otros datos que pide esta etiqueta
                  </span>
                  <div style={{ marginTop: 2, fontSize: 12.5, color: c.texto3, textWrap: 'pretty' }}>
                    Los que la plantilla elegida usa además de los de arriba.
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 10 }}>
                  {lista.otras.map((f) => (
                    <Renglon key={f.clave} nombre={f.nombre} enLaEtiqueta>
                      <CampoConRecientes
                        clave={f.clave}
                        valor={valores[f.clave] ?? ''}
                        recientes={cat.recientes[f.clave] ?? []}
                        onCambiar={(v) => poner({ [f.clave]: v })}
                      />
                    </Renglon>
                  ))}
                </div>
              </motion.div>
            )}
            </AnimatePresence>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="button"
                onClick={generar}
                disabled={!plantilla || faltan.length > 0}
                className="flex-1 tablet:flex-none"
                style={{
                  ...botonSecundario,
                  minHeight: 44,
                  padding: '0 18px',
                  justifyContent: 'center',
                  opacity: !plantilla || faltan.length ? 0.55 : 1,
                }}
              >
                <Icono nombre="visibility" size={19} />
                Ver la etiqueta
              </button>

              <AnimatePresence initial={false}>
                {emitida && (
                  <motion.button
                    key="imprimir"
                    type="button"
                    onClick={imprimir}
                    {...abrir(10)}
                    className="flex-1 tablet:flex-none"
                    style={{ ...botonPrimario, minHeight: 44, padding: '0 18px', justifyContent: 'center' }}
                  >
                    <Icono nombre="print" size={19} />
                    Imprimir {cantidad > 1 ? `${cantidad} etiquetas` : 'la etiqueta'}
                  </motion.button>
                )}
              </AnimatePresence>

              <AnimatePresence initial={false}>
                {faltan.length > 0 && (
                  <motion.span {...desplegar} style={{ ...desplegar.style, fontSize: 12.5, color: c.ambar, flex: '1 1 100%' }}>
                    Falta {faltan.map((f) => f.nombre).join(', ')}.
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
          </div>

          <AnimatePresence>
          {emitida && (
            <motion.div key="vista" {...abrir(14)} style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <span style={{ fontSize: 14, fontWeight: 600, color: c.texto }}>Así se va a imprimir</span>
                <div style={{ marginTop: 2, fontSize: 12.5, color: c.texto3 }}>
                  {emitida.plantilla.ancho} × {emitida.plantilla.alto} mm
                  {emitida.plantilla.orientacion === 'apaisada' && ' · apaisada'} · <Numero valor={cantidad} />{' '}
                  {cantidad === 1 ? 'copia' : 'copias'}
                </div>
              </div>
              <div
                id="wms-etq-manual"
                style={{
                  border: `1px solid ${c.borde}`,
                  borderRadius: 8,
                  background: '#fff',
                  overflow: 'hidden',
                  lineHeight: 0,
                  alignSelf: 'flex-start',
                  maxWidth: '100%',
                }}
              >
                <Etiqueta
                  plantilla={emitida.plantilla.plantilla}
                  ancho={emitida.plantilla.ancho}
                  alto={emitida.plantilla.alto}
                  orientacion={emitida.plantilla.orientacion}
                  datos={emitida.datos}
                  style={{ display: 'block', maxWidth: '100%', height: 'auto' }}
                />
              </div>
            </motion.div>
          )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
}
